import { KeyboardArrowDown } from '@mui/icons-material'
import {
	Button,
	Checkbox,
	FormControlLabel,
	Menu,
	MenuItem,
} from '@mui/material'
import { ChangeEvent, ReactNode, useMemo, useRef, useState } from 'react'

interface MultiSelectProps<T> {
	label: string
	onChange: (value: T[]) => void
	options: { label?: ReactNode; value: T }[]
	value: T[]
}

// displays a button with a menu that has all the multiselect options
// that can be chosen with checkboxes - inspired by Devias Material Kit Pro
export const MultiSelect = <T extends string | number>(props: MultiSelectProps<T>) => {
	const anchorRef = useRef<HTMLButtonElement | null>(null)
	const [openMenu, setOpenMenu] = useState<boolean>(false)
	const dataType = useMemo(() => typeof props.options[0]?.value, [props.options])

	const isChecked = useMemo(() => (currentValues: T[], option: T) =>
		currentValues.includes(option)
		, [])

	const handleOpenMenu = () =>
		setOpenMenu(true)

	const handleCloseMenu = () =>
		setOpenMenu(false)

	const handleChange = (event: ChangeEvent<HTMLInputElement>): void => {
		let newValue = [...props.value]
		if (event.target.checked)
			newValue.push((dataType === "number" ? Number(event.target.value) : event.target.value) as T)
		else newValue = newValue.filter((item) => item != event.target.value)

		props.onChange(newValue)
	}

	return (
		<>
			<Button
				color="inherit"
				endIcon={<KeyboardArrowDown fontSize="small" />}
				onClick={handleOpenMenu}
				ref={anchorRef}
			>
				{props.label}
			</Button>
			<Menu
				anchorEl={anchorRef.current}
				onClose={handleCloseMenu}
				open={openMenu}
				PaperProps={{ style: { width: 250 } }}
			>
				{props.options.map((option) => (
					<MenuItem key={option.value}>
						<FormControlLabel
							control={
								<Checkbox
									checked={isChecked(props.value, option.value)}
									onChange={handleChange}
									value={option.value}
								/>
							}
							label={option.label ?? option.value}
							sx={{
								flexGrow: 1,
								mr: 0,
							}}
						/>
					</MenuItem>
				))}
			</Menu>
		</>
	)
}
